import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuditCatalogue } from '../../context/AuditCatalogueContext';
import { auditApi } from '../../services/auditApi';
import { AuditEntry, AuditEntryDetail } from '../../types/audit';
import ActorChip from './ActorChip';
import { formatGst, humanizeEntityType, visibleEntityRoute } from './auditFormat';

interface AuditDetailDrawerProps {
  /** The list row; the drawer shows it at once and fills changes/details/trace id from the detail call. */
  row: AuditEntry;
  onClose: () => void;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

function prettyDetails(details: string | null | undefined): string | null {
  if (!details) return null;
  try {
    return JSON.stringify(JSON.parse(details), null, 2);
  } catch {
    return details;
  }
}

/**
 * Spec 069 AC-B4 — the right-hand drawer with every stored field, the
 * changes table and a copyable trace id. Focus is trapped inside; Escape and the close button close it
 * and focus returns to the row that opened it.
 */
export default function AuditDetailDrawer({ row, onClose }: AuditDetailDrawerProps) {
  const { token, isSuperAdmin, hasScreen } = useAuth();
  const { labelOf } = useAuditCatalogue();
  const [detail, setDetail] = useState<AuditEntryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // A parent re-render passes a new onClose; the focus trap must not re-run (and steal focus) for that.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!token) return;
    let live = true;
    setDetail(null);
    setError(null);
    auditApi.get(row.id, token)
      .then((d) => { if (live) setDetail(d); })
      .catch((err) => { if (live) setError(err instanceof Error ? err.message : 'Could not load the details'); });
    return () => { live = false; };
  }, [row.id, token]);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []);

  const d: AuditEntry & Partial<AuditEntryDetail> = { ...row, ...(detail ?? {}) };
  const route = visibleEntityRoute(d, isSuperAdmin, hasScreen);
  const details = prettyDetails(d.details);

  const copyTrace = async () => {
    if (!d.traceId) return;
    try {
      await navigator.clipboard.writeText(d.traceId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 3000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="admin-modal-overlay audit-drawer-overlay" onClick={onClose}>
      <div
        ref={panelRef}
        className="admin-modal audit-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h3 id="audit-drawer-title" style={{ margin: 0 }}>{d.actionLabel || labelOf(d.action)}</h3>
          <button ref={closeRef} type="button" className="admin-modal-close" aria-label="Close details" onClick={onClose}>&times;</button>
        </div>

        <div className="admin-modal-body audit-drawer__body">
          {error && <div className="admin-info-banner danger" role="alert">{error}</div>}

          <dl className="audit-dl">
            <dt>When</dt><dd>{formatGst(d.performedAt)}</dd>
            <dt>Admin</dt><dd><ActorChip role={d.actorRole ?? 'ADMIN'} name={d.actorName} subjectId={d.actorSubjectId} /></dd>
            <dt>Action</dt><dd>{d.actionLabel || labelOf(d.action)} <code className="audit-code">{d.action}</code></dd>
            {d.group && <><dt>Group</dt><dd>{humanizeEntityType(d.group)}</dd></>}
            <dt>Entity</dt>
            <dd>
              {humanizeEntityType(d.entityType)}{' '}
              {route ? <Link to={route} className="audit-link">{d.entityName || d.entityId || 'open'}</Link> : (d.entityName || '—')}
              {d.entityId && <div><code className="audit-code">{d.entityId}</code></div>}
            </dd>
            {(d.garageName || d.garageId) && (
              <>
                <dt>Garage</dt>
                <dd>
                  {d.garageId && hasScreen('garages')
                    ? <Link to={`/garages/${d.garageId}`} className="audit-link">{d.garageName || d.garageId}</Link>
                    : (d.garageName || d.garageId)}
                </dd>
              </>
            )}
            <dt>Source</dt><dd>{d.sourceService ?? '—'}</dd>
            <dt>Request id</dt>
            <dd>
              {d.traceId ? (
                <span className="audit-trace">
                  <code className="audit-code">{d.traceId}</code>
                  <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={copyTrace}>
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <span className="sr-only" aria-live="polite">{copied ? 'Request id copied' : ''}</span>
                </span>
              ) : (detail ? '—' : 'Loading…')}
            </dd>
          </dl>

          <h4 className="audit-drawer__h">Changes</h4>
          {!detail && !error ? (
            <p className="audit-hint">Loading…</p>
          ) : d.changes && d.changes.length > 0 ? (
            <div className="admin-table-wrapper">
              <table className="admin-table audit-changes">
                <thead><tr><th scope="col">Field</th><th scope="col">Before</th><th scope="col">After</th></tr></thead>
                <tbody>
                  {d.changes.map((c, i) => (
                    <tr key={`${c.field}-${i}`}>
                      <td>{c.label || c.field}</td>
                      <td className="audit-change--before">{c.before ?? '—'}</td>
                      <td className="audit-change--after">{c.after ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="audit-hint">No field changes recorded for this action.</p>
          )}

          {details && (
            <>
              <h4 className="audit-drawer__h">Details</h4>
              <pre className="audit-pre">{details}</pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
